import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings, User, FileText, LogOut, ChevronRight, Camera, Crown, Gift, ArrowLeft, BarChart3, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'
import { isMobileBrowser, openMobileApp } from '@/utils/deviceDetection'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, logout, getToken } = useAuth()
  const [currentSubscription, setCurrentSubscription] = useState<any>(null)
  const [usage, setUsage] = useState<any>(null)
  const [stats, setStats] = useState<any>(null)
  const [lastFetchTime, setLastFetchTime] = useState<number>(0)
  const hasInitialized = useRef(false)
  
  const isReferrer = user?.type === 'referrer'
  const isJobSeeker = user?.type === 'jobseeker'
  const profilePhotoUrl = user?.photoURL

  const CACHE_DURATION = 60000 // 1 minute cache

  const getPlanColors = (planName: string) => {
    switch (planName) {
      case 'free': return { from: 'from-green-400', to: 'to-green-600' }
      case 'basic': return { from: 'from-blue-400', to: 'to-blue-600' }
      case 'premium': return { from: 'from-amber-400', to: 'to-amber-600' }
      default: return { from: 'from-blue-400', to: 'to-blue-600' }
    }
  }
  
  useEffect(() => {
    // Prevent duplicate calls in React 18 Strict Mode
    if (hasInitialized.current) return
    
    if (user?.uid && isJobSeeker) {
      hasInitialized.current = true
      fetchSubscriptionData()
      fetchInviteStats()
    }
  }, [user?.uid, isJobSeeker])

  const fetchSubscriptionData = async (forceRefresh = false) => {
    try {
      const now = Date.now()
      
      // Use cached data if available and not forcing refresh
      if (!forceRefresh && now - lastFetchTime < CACHE_DURATION && currentSubscription && usage) {
        console.log('📦 ProfilePage: Using cached subscription data')
        return
      }

      console.log('🔄 ProfilePage: Fetching fresh subscription data')
      const token = await getToken()
      if (!token) return

      const response = await api.getCurrentSubscription(token)
      if (response.success) {
        setCurrentSubscription(response.subscription)
        setUsage(response.usage)
        setLastFetchTime(now)
      }
    } catch (error) {
      console.error('Error fetching subscription:', error)
    }
  }

  const fetchInviteStats = async (forceRefresh = false) => {
    try {
      const now = Date.now()
      
      // Use cached data if available and not forcing refresh
      if (!forceRefresh && now - lastFetchTime < CACHE_DURATION && stats) {
        console.log('📦 ProfilePage: Using cached invite stats')
        return
      }

      console.log('🔄 ProfilePage: Fetching fresh invite stats')
      const token = await getToken()
      if (!token) return

      const response = await api.getMyInvites(token)
      if (response.success) {
        setStats(response.stats)
      }
    } catch (error) {
      console.error('Error fetching invite stats:', error)
    }
  }
  
  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with Settings Button */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-poppins font-bold text-gray-900">Profile</h1>
        <button
          onClick={() => navigate('/settings')}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Settings className="h-6 w-6 text-gray-600" />
        </button>
      </div>
      
      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 mb-6">
        <div className="flex flex-col items-center">
          {/* Profile Image */}
          <div className="relative mb-4">
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center">
                <User className="h-12 w-12 text-white" />
              </div>
            )}
            <button
              onClick={() => navigate('/photo')}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center border-2 border-white shadow-md hover:bg-primary-600 transition-colors"
            >
              <Camera className="h-4 w-4 text-white" />
            </button>
          </div>
          
          <h2 className="text-xl font-poppins font-semibold text-gray-900 mb-1">
            {user?.displayName || 'User'}
          </h2>
          <p className="text-sm text-gray-600 mb-1">
            {isReferrer ? 'Referrer' : 'Job Seeker'}
          </p>
          <p className="text-sm text-gray-500 mb-4 break-all max-w-full overflow-hidden text-center">
            {user?.email || 'No email provided'}
          </p>
          
          {isReferrer && (
            <div className="text-center">
              <p className="text-base font-medium text-gray-700">
                {user?.companyName || 'Company'}
              </p>
              <p className="text-sm text-gray-600">
                {user?.positionTitle || 'Position'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Details - Job Seeker Only */}
      {isJobSeeker && (currentSubscription || usage) && (() => {
        const planName = currentSubscription?.planName || 'basic'
        const colors = getPlanColors(planName)
        
        return (
          <div className={`bg-gradient-to-r ${colors.from} ${colors.to} rounded-xl p-6 text-white mb-6`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Crown className="h-6 w-6" />
                <h2 className="ml-2 text-xl font-bold">
                  {currentSubscription?.planDisplayName || currentSubscription?.planName || 'Basic Plan'}
                </h2>
              </div>
              {currentSubscription?.status === 'active' && (
                <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
                  Active
                </span>
              )}
            </div>
          
          {usage && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="text-center">
                  <p className="text-2xl font-bold">{usage.requestsUsedThisMonth}</p>
                  <p className="text-sm text-white text-opacity-80 mt-1">Used</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{usage.requestsRemaining}</p>
                  <p className="text-sm text-white text-opacity-80 mt-1">Remaining</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{usage.referralRequestsLimit}</p>
                  <p className="text-sm text-white text-opacity-80 mt-1">Total</p>
                </div>
              </div>
              
              {currentSubscription?.currentPeriodEnd && (
                <div className="text-center mb-3">
                  <p className="text-sm text-white text-opacity-80">
                    {currentSubscription.cancelAtPeriodEnd ? 'Cancels on' : 'Renews on'}{' '}
                    <span className="font-medium">{new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}</span>
                  </p>
                </div>
              )}
              
              {usage.isPriority ? (
                <div className="flex items-center justify-center bg-white bg-opacity-20 rounded-lg py-2 px-3">
                  <BarChart3 className="h-4 w-4" />
                  <span className="ml-2 text-sm font-medium">Priority Matching Enabled</span>
                </div>
              ) : (
                <div className="flex items-center justify-center bg-white bg-opacity-15 border border-white border-opacity-30 rounded-lg py-2 px-3">
                  <X className="h-4 w-4 text-white text-opacity-60" />
                  <span className="ml-2 text-sm font-medium text-white text-opacity-70">Priority Matching Not Enabled</span>
                </div>
              )}
            </>
          )}
          </div>
        )
      })()}
      
      {/* Menu Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Job Seeker Specific Sections */}
        {isJobSeeker && (
          <>
            {/* Subscription */}
            <button
              onClick={() => navigate('/subscription')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <div className="flex items-center flex-1">
                <Crown className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div className="ml-3 text-left flex-1">
                  <p className="text-base font-medium text-gray-700">
                    {currentSubscription ? 'Manage Subscription' : 'Upgrade to Premium'}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {usage 
                      ? `${usage.requestsUsedThisMonth}/${usage.referralRequestsLimit} requests used`
                      : 'Get more referral requests'
                    }
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
            </button>

            {/* Invite & Rewards */}
            <button
              onClick={() => navigate('/invite-friends')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <div className="flex items-center flex-1">
                <Gift className="h-5 w-5 text-green-500 flex-shrink-0" />
                <div className="ml-3 text-left flex-1">
                  <p className="text-base font-medium text-gray-700">Invite & Rewards</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {stats 
                      ? `${stats.verifiedReferrers}/3 verified referrers`
                      : 'Invite friends for premium rewards'
                    }
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
            </button>
          </>
        )}

        {/* Referrer Specific Sections - Placeholder (not in scope) */}
        {isReferrer && (
          <button
            disabled
            className="w-full flex items-center justify-between p-4 opacity-50 cursor-not-allowed border-b border-gray-100"
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-green-500" />
              <p className="ml-3 text-base font-medium text-gray-700">Company Questions</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        )}

        {/* Settings */}
        <button
          onClick={() => navigate('/settings')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
        >
          <div className="flex items-center">
            <Settings className="h-5 w-5 text-green-500" />
            <p className="ml-3 text-base font-medium text-gray-700">Settings</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </button>

        {/* Back to Mobile App - Mobile Browser Only */}
        {isMobileBrowser() && (
          <button
            onClick={() => openMobileApp('profile')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div className="flex items-center">
              <ArrowLeft className="h-5 w-5 text-blue-500" />
              <p className="ml-3 text-base font-medium text-gray-700">Back to Mobile App</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </button>
        )}
        
        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center">
            <LogOut className="h-5 w-5 text-red-500" />
            <p className="ml-3 text-base font-medium text-red-500">Logout</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </button>
      </div>
    </div>
  )
}
